import {
  CREATE_LAYER,
  UPDATE_LAYER,
  DELETE_LAYER,
  DELETE_LAYERS,
  CHANGE_SORT_ORDER,
  FETCH_LAYER_OPTIONS,
  FETCH_LAYER_OPTIONS_SUCCESS,
  FETCH_LAYER_OPTIONS_FAILURE,
  FETCH_EXISTING_SKETCH_SUCCESS,
  UPDATE_SKETCH,
  SAVE_SKETCH_SUCCESS,
  FETCH_LAYER_DATA,
  FETCH_LAYER_DATA_SUCCESS,
  FETCH_LAYER_DATA_FAILURE,
  REPLACE_LAYERS,
  UPDATE_ZOOM,
  UPDATE_MEASUREMENT_FONT_SIZE,
  UPDATE_CENTER,
  SET_SELECTED_LAYER_ID,
  SET_SELECTED_POPUP_LAYER_ID,
  SET_MODE,
  CREATE_TAPE_FEATURE,
  UPDATE_TAPE_FEATURE,
  DELETE_TAPE_FEATURE,
  REPLACE_TAPE_FEATURES,
  UNDO_TAPE_FEATURES,
  REDO_TAPE_FEATURES,
  PUSH_TAPE_FEATURES_HISTORY,
  SET_SELECTED_TAPE_FEATURE_ID,
  FETCH_EXISTING_TAPE_SKETCH_SUCCESS,
  SAVE_TAPE_SKETCH_SUCCESS,
  FETCH_TAPE_SKETCH_CONFIG_SUCCESS,
  SHIFT_TAPE_SKETCH_ORIGIN,
  SET_TAPE_SKETCH_BEARING,
} from './constants';
import { mapCoordinatesArrayToObject, getPoints, getArea, getSqFt } from './utils';
import { uid, measureSqft, livingSqft, getFeatureTypeCode, getGarageKindName } from './TapeTool/tapeUtils';

// Snapshots the current tapeFeatures onto a history stack (used for both the undo history and
// the redo stack), capped at 40 entries. Every mutation that pushes a NEW history entry also
// clears the OTHER stack passed as clearedStack's key, matching standard undo/redo semantics —
// making a fresh edit invalidates any pending redo.
function pushHistory(history, snapshot) {
  return [...(history || []).slice(-40), snapshot];
}

// Recomputes footprintSqft/livingSqft for a tape feature given the current tapeConfig.
function withComputedSqft(feature, tapeConfig) {
  const typeCode = getFeatureTypeCode(feature.tapeFeatureTypeId, tapeConfig);
  const garageKindName = getGarageKindName(feature.tapeGarageKindId, tapeConfig);
  return {
    ...feature,
    footprintSqft: Math.round(measureSqft(feature.vertices)),
    livingSqft: Math.round(livingSqft(feature, typeCode, garageKindName)),
  };
}

export const sketchDefaultZoomLevel = 20;
export const sketchDefaultMeasurementFontSize = 12;

export const reducer = (state, action) => {
  switch (action.type) {
    case CREATE_LAYER:
      return {
        ...state,
        layers: state.layers.concat({
          ...action.layer,
          layerOption: state.layerData.find(layerData => action.layer.sketchAreaId === layerData.sketchAreaId),
          area: getArea(action.layer.latLngs),
          sqFt: getSqFt(action.layer.latLngs),
        }),
      };
    case UPDATE_LAYER:
      const updateLayerIndex = state.layers.findIndex(layer => action.layerId === layer.id);
      const updatedLayers = state.layers.slice();

      updatedLayers[updateLayerIndex] = {
        ...updatedLayers[updateLayerIndex],
        [action.key]: action.val,
      };

      // recalculate area and square footage if the lat/lngs change
      if (action.key === 'latLngs') {
        updatedLayers[updateLayerIndex] = {
          ...updatedLayers[updateLayerIndex],
          area: getArea(action.val),
          sqFt: getSqFt(action.val),
        };
      } else if (
        updatedLayers[updateLayerIndex] &&
        updatedLayers[updateLayerIndex].layerOption &&
        updatedLayers[updateLayerIndex].layerOption.sketchAreaId === 6
      ) {
        // this is a story height (account for stories in the square footage)
        const storyOption = updatedLayers[updateLayerIndex].layerOption.subArea.find(
          sub => sub.sketchSubAreaId === updatedLayers[updateLayerIndex].sketchSubAreaId,
        );

        let stories = 1;

        if (storyOption) {
          stories = Number(storyOption.description);
        }

        const latLngs = updatedLayers[updateLayerIndex].latLngs;

        updatedLayers[updateLayerIndex] = {
          ...updatedLayers[updateLayerIndex],
          area: getArea(latLngs),
          sqFt: getSqFt(latLngs, stories),
        };
      }

      return {
        ...state,
        layers: updatedLayers
          // layers: state.layers
          // .map(layer => (action.layerId === layer.id ? { ...layer, [action.key]: action.val } : layer))
          .map(layer => (action.layerId && !layer.leafletId ? { ...layer, leafletId: action.val } : layer)),
      };
    case REPLACE_LAYERS:
      return {
        ...state,
        layers: action.layers,
      };
    case DELETE_LAYER:
      return {
        ...state,
        layers: state.layers.filter(layer => action.layerId !== layer.id),
      };
    case DELETE_LAYERS:
      return {
        ...state,
        layers: state.layers.filter(layer => !action.layerIds.includes(layer.id)),
      };
    case CHANGE_SORT_ORDER:
      return {
        ...state,
        layers: action.layers,
      };
    case FETCH_EXISTING_SKETCH_SUCCESS:
      return {
        ...state,
        sketch: action.sketch,
        center: action.center,
        layers: action.layers.map(layer => ({
          ...layer,
          enabled: true,
          layerOption: state.layerData.find(layerData => layer.sketchAreaId === layerData.sketchAreaId),
        })),
        zoomLevel: action.zoomLevel || sketchDefaultZoomLevel,
        measurementFontSize: action.measurementFontSize || sketchDefaultMeasurementFontSize,
      };
    case FETCH_LAYER_DATA:
      return {
        ...state,
        loading: true,
      };
    case FETCH_LAYER_DATA_SUCCESS:
      return {
        ...state,
        loading: false,
        layerData: action.layerData,
      };
    case FETCH_LAYER_DATA_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    case UPDATE_SKETCH:
      return {
        ...state,
        sketch: { ...state.sketch, ...action.sketchData },
      };

    case UPDATE_ZOOM:
      return {
        ...state,
        zoomLevel: action.zoomLevel || sketchDefaultZoomLevel,
      };
    case UPDATE_MEASUREMENT_FONT_SIZE:
      return {
        ...state,
        measurementFontSize: action.measurementFontSize || sketchDefaultMeasurementFontSize,
      };
    case UPDATE_CENTER:
      return {
        ...state,
        center: action.center,
      };
    case SET_SELECTED_LAYER_ID:
      return {
        ...state,
        selectedLayerId: action.layerId,
      };
    case SET_SELECTED_POPUP_LAYER_ID:
      return {
        ...state,
        selectedPopupLayerId: action.layerId,
      };
    case SET_MODE:
      return {
        ...state,
        mode: action.mode,
      };
    case CREATE_TAPE_FEATURE:
      return {
        ...state,
        tapeFeatures: state.tapeFeatures.concat(withComputedSqft({ id: uid(), ...action.feature }, state.tapeConfig)),
        tapeFeaturesHistory: pushHistory(state.tapeFeaturesHistory, state.tapeFeatures),
        tapeFeaturesRedoStack: [],
      };
    case UPDATE_TAPE_FEATURE: {
      const updateIndex = state.tapeFeatures.findIndex(feature => action.featureId === feature.id);
      if (updateIndex === -1) {
        return state;
      }
      const updatedTapeFeatures = state.tapeFeatures.slice();
      updatedTapeFeatures[updateIndex] = withComputedSqft(
        { ...updatedTapeFeatures[updateIndex], [action.key]: action.val },
        state.tapeConfig,
      );
      return {
        ...state,
        tapeFeatures: updatedTapeFeatures,
      };
    }
    case DELETE_TAPE_FEATURE:
      return {
        ...state,
        tapeFeatures: state.tapeFeatures.filter(feature => action.featureId !== feature.id),
        selectedTapeFeatureId: state.selectedTapeFeatureId === action.featureId ? null : state.selectedTapeFeatureId,
        tapeFeaturesHistory: pushHistory(state.tapeFeaturesHistory, state.tapeFeatures),
        tapeFeaturesRedoStack: [],
      };
    case REPLACE_TAPE_FEATURES:
      return {
        ...state,
        tapeFeatures: action.tapeFeatures,
        tapeFeaturesHistory: pushHistory(state.tapeFeaturesHistory, state.tapeFeatures),
        tapeFeaturesRedoStack: [],
      };
    case PUSH_TAPE_FEATURES_HISTORY:
      return {
        ...state,
        tapeFeaturesHistory: pushHistory(state.tapeFeaturesHistory, state.tapeFeatures),
        tapeFeaturesRedoStack: [],
      };
    case UNDO_TAPE_FEATURES: {
      const history = state.tapeFeaturesHistory || [];
      if (!history.length) {
        return state;
      }
      return {
        ...state,
        tapeFeatures: history[history.length - 1],
        tapeFeaturesHistory: history.slice(0, -1),
        tapeFeaturesRedoStack: pushHistory(state.tapeFeaturesRedoStack, state.tapeFeatures),
        selectedTapeFeatureId: null,
      };
    }
    case REDO_TAPE_FEATURES: {
      const redoStack = state.tapeFeaturesRedoStack || [];
      if (!redoStack.length) {
        return state;
      }
      return {
        ...state,
        tapeFeatures: redoStack[redoStack.length - 1],
        tapeFeaturesRedoStack: redoStack.slice(0, -1),
        tapeFeaturesHistory: pushHistory(state.tapeFeaturesHistory, state.tapeFeatures),
        selectedTapeFeatureId: null,
      };
    }
    case SET_SELECTED_TAPE_FEATURE_ID:
      return {
        ...state,
        selectedTapeFeatureId: action.featureId,
      };
    case FETCH_EXISTING_TAPE_SKETCH_SUCCESS:
      return {
        ...state,
        tapeSketch: action.tapeSketch,
        tapeFeatures: (action.tapeSketch?.features || []).map(feature => ({
          id: uid(),
          tapeFeatureTypeId: feature.tapeFeatureTypeId,
          tapeFoundationTypeId: feature.tapeFoundationTypeId,
          tapeGarageKindId: feature.tapeGarageKindId,
          stories: feature.stories,
          storiesAbove: feature.storiesAbove,
          isCathedral: feature.isCathedral,
          vertices: (feature.vertices || []).slice().sort((a, b) => a.sortOrder - b.sortOrder).map(v => ({ x: v.x, y: v.y })),
          footprintSqft: feature.footprintSqft,
          livingSqft: feature.livingSqft,
        })),
      };
    case SAVE_TAPE_SKETCH_SUCCESS:
      return {
        ...state,
        tapeSketch: action.tapeSketch,
      };
    case FETCH_TAPE_SKETCH_CONFIG_SUCCESS:
      return {
        ...state,
        tapeConfig: action.tapeConfig,
      };
    case SHIFT_TAPE_SKETCH_ORIGIN: {
      if (!state.tapeSketch) {
        return state;
      }
      return {
        ...state,
        tapeSketch: {
          ...state.tapeSketch,
          originLat: (state.tapeSketch.originLat ?? 0) + action.deltaLat,
          originLng: (state.tapeSketch.originLng ?? 0) + action.deltaLng,
        },
      };
    }
    case SET_TAPE_SKETCH_BEARING: {
      if (!state.tapeSketch) {
        return state;
      }
      return {
        ...state,
        tapeSketch: {
          ...state.tapeSketch,
          bearing: action.bearing,
        },
      };
    }
    default:
      return state;
  }
};

export default reducer;
